import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import DossiersTabs from "@/components/DossiersTabs";
import { api, formatEURCompact } from "@/lib/api";
import {
  Moon, Buildings, Star, ArrowClockwise, Clock, ArrowRight,
  CalendarPlus, X, ShieldCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * V4.27 — Page Stand-by, vue portefeuille (style proche "En montage").
 *
 * Doctrine : stand-by = file d'attente intelligente, pas poubelle.
 * Chaque carte permet de décider : réactiver / reporter / laisser dormir.
 *
 * Cartes :
 *   - badge statut de reprise (À reprendre / À revoir / Reprise le XX/XX / Sans date)
 *   - nom + banques + motif stand-by
 *   - CA potentiel + étoiles
 *   - actions rapides : Réactiver · Reporter (+7j / +14j / +30j / date custom)
 *
 * Tri : À reprendre > À revoir > Reprise proche > Sans date > Reprise lointaine.
 *       Puis CA desc, étoiles desc.
 *
 * Source : GET /api/dossiers?standby=true
 * Mutations :
 *   - Réactiver : POST /api/dossiers/{id}/reactivate
 *   - Reporter  : POST /api/dossiers/{id}/standby (re-update reason + until)
 */

const STALE_THRESHOLD_DAYS = 30; // sans date de reprise + ancienneté > X → "À revoir"

const REPRISE_KIND = {
  due: { label: "À reprendre", bg: "var(--cf-danger-soft)", fg: "var(--cf-danger-text)", priority: 1 },
  review: { label: "À revoir", bg: "var(--cf-warning-soft)", fg: "var(--cf-warning-text)", priority: 2 },
  soon: { label: "Reprise prévue", bg: "var(--cf-primary-soft)", fg: "var(--cf-primary-hover)", priority: 3 },
  none: { label: "Sans date de reprise", bg: "var(--cf-bg-2)", fg: "var(--cf-neutral-text)", priority: 4 },
  later: { label: "Reprise prévue", bg: "var(--cf-primary-soft)", fg: "var(--cf-primary-text)", priority: 5 },
};

function getRepriseInfo(d, todayIso) {
  const until = d.standby_until ? d.standby_until.slice(0, 10) : null;
  if (until && until <= todayIso) {
    return { kind: "due", label: REPRISE_KIND.due.label, dateLabel: null };
  }
  if (until) {
    // Soon : <= 14 jours, sinon later
    const today = new Date(todayIso);
    const target = new Date(until);
    const diffDays = Math.round((target - today) / (1000 * 60 * 60 * 24));
    const formatted = new Date(until + "T00:00:00").toLocaleDateString("fr-FR");
    if (diffDays <= 14) {
      return { kind: "soon", label: `Reprise ${formatted}`, dateLabel: formatted };
    }
    return { kind: "later", label: `Reprise ${formatted}`, dateLabel: formatted };
  }
  // Pas de date : si ancienneté > seuil → review
  if (d.standby_created_at) {
    const created = new Date(d.standby_created_at);
    const today = new Date(todayIso);
    const age = Math.round((today - created) / (1000 * 60 * 60 * 24));
    if (age >= STALE_THRESHOLD_DAYS) {
      return { kind: "review", label: REPRISE_KIND.review.label, dateLabel: `${age}j en sommeil` };
    }
  }
  return { kind: "none", label: REPRISE_KIND.none.label, dateLabel: null };
}

function StagePill({ d }) {
  if (d.mandat_signe && d.date_envoi_banque) {
    return <Pill bg="var(--cf-primary-soft)" fg="var(--cf-primary-hover)" label="En banque" />;
  }
  if (d.mandat_signe) return <Pill bg="var(--cf-warning-soft)" fg="var(--cf-warning-text)" label="À envoyer" />;
  if (d.project_maturity === "offre_en_cours") return <Pill bg="var(--cf-warning-soft)" fg="var(--cf-warning-text)" label="Offre en cours" />;
  if (d.project_maturity === "en_recherche") return <Pill bg="var(--cf-primary-soft)" fg="var(--cf-primary-hover)" label="En recherche" />;
  if (d.project_maturity === "en_reflexion") return <Pill bg="var(--cf-bg-2)" fg="var(--cf-neutral-text)" label="En réflexion" />;
  return <Pill bg="var(--cf-bg-2)" fg="var(--cf-neutral-text)" label="Prospect" />;
}

function Pill({ bg, fg, label, icon }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[10.5px] font-bold rounded-md px-1.5 py-0.5 whitespace-nowrap"
      style={{ background: bg, color: fg }}
    >
      {icon}
      {label}
    </span>
  );
}

function ReporterMenu({ open, onClose, onPick, anchorRect }) {
  if (!open) return null;
  const choices = [
    { days: 7, label: "+7 jours" },
    { days: 14, label: "+14 jours" },
    { days: 30, label: "+30 jours" },
  ];
  return (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div
        className="absolute right-0 top-full mt-1 z-40 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden w-44"
        data-testid="reporter-menu"
      >
        {choices.map((c) => (
          <button
            key={c.days}
            type="button"
            onClick={() => onPick(c.days)}
            className="w-full px-3 py-2 text-left text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50"
            data-testid={`reporter-+${c.days}`}
          >
            {c.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onPick("custom")}
          className="w-full px-3 py-2 text-left text-[12.5px] font-semibold text-violet-700 hover:bg-violet-50 border-t border-slate-100"
          data-testid="reporter-custom"
        >
          Date personnalisée…
        </button>
      </div>
    </>
  );
}

function StandbyCard({ d, todayIso, onReactivate, onReporter }) {
  const navigate = useNavigate();
  const reprise = getRepriseInfo(d, todayIso);
  const repriseStyle = REPRISE_KIND[reprise.kind];
  const [menuOpen, setMenuOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customDate, setCustomDate] = useState("");

  const handleReactivate = async (e) => {
    e.stopPropagation();
    try {
      await api.post(`/dossiers/${d.id}/reactivate`);
      toast.success("Dossier réactivé");
      onReactivate(d.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la réactivation");
    }
  };

  const handleReporter = async (choice) => {
    setMenuOpen(false);
    if (choice === "custom") {
      setCustomOpen(true);
      return;
    }
    const newUntil = new Date();
    newUntil.setDate(newUntil.getDate() + choice);
    const iso = newUntil.toISOString().slice(0, 10);
    await saveReporter(iso);
  };

  const saveReporter = async (iso) => {
    try {
      await api.post(`/dossiers/${d.id}/standby`, {
        reason: d.standby_reason || "Reporté",
        until: iso,
      });
      toast.success(`Reprise reportée au ${new Date(iso + "T00:00:00").toLocaleDateString("fr-FR")}`);
      onReporter(d.id, iso);
      setCustomOpen(false);
      setCustomDate("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors du report");
    }
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-slate-300 transition-colors"
      data-testid={`standby-card-${d.id}`}
    >
      {/* Top : badge reprise + nom + CA */}
      <div
        className="px-3 py-2.5 flex items-start gap-2.5 cursor-pointer"
        onClick={() => navigate(`/dossier/${d.id}`)}
      >
        {/* Badge reprise pleine hauteur */}
        <span
          className="flex-shrink-0 inline-flex items-center gap-1 text-[10.5px] font-extrabold rounded-md px-2 py-1 whitespace-nowrap"
          style={{ background: repriseStyle.bg, color: repriseStyle.fg }}
          data-testid={`standby-reprise-${d.id}`}
        >
          {reprise.kind === "due" && <Clock size={11} weight="fill" />}
          {reprise.kind === "soon" && <CalendarPlus size={11} weight="fill" />}
          {reprise.kind === "later" && <CalendarPlus size={11} weight="fill" />}
          {reprise.kind === "review" && <Moon size={11} weight="fill" />}
          {reprise.kind === "none" && <Moon size={11} weight="fill" />}
          {reprise.label}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-bold text-[13.5px] text-slate-900 truncate">{d.nom}</span>
            {d.banques?.length > 0 && (
              <span className="text-[10.5px] text-slate-500 flex items-center gap-0.5">
                <Buildings size={9} /> {d.banques.slice(0, 2).join(" / ")}
                {d.banques.length > 2 && " +" + (d.banques.length - 2)}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
            <StagePill d={d} />
            {d.mandat_signe && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold rounded-md px-1.5 py-0.5 bg-[color:var(--cf-success-soft)] text-[color:var(--cf-success-text)]">
                <ShieldCheck size={10} weight="fill" /> Mandat
              </span>
            )}
            {reprise.dateLabel && reprise.kind !== "soon" && reprise.kind !== "later" && (
              <span className="text-[10px] text-slate-500 font-semibold">{reprise.dateLabel}</span>
            )}
          </div>
          {d.standby_reason && (
            <div className="mt-1 text-[11.5px] text-slate-600">
              <span className="font-semibold text-slate-500">Motif :</span> {d.standby_reason}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 text-right">
          <div className="text-[14px] font-extrabold text-[color:var(--cf-success)] leading-tight tabular-nums">
            {formatEURCompact(d.ca_net)}
          </div>
          {d.etoiles > 0 && (
            <div className="mt-0.5 inline-flex items-center gap-0.5">
              {[1, 2, 3].map((n) => (
                <Star
                  key={n}
                  size={11}
                  weight={n <= d.etoiles ? "fill" : "regular"}
                  color={n <= d.etoiles ? "var(--cf-warning)" : "var(--cf-border-strong)"}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* CTAs : Réactiver + Reporter */}
      <div className="grid grid-cols-2 border-t border-slate-100 relative">
        <button
          type="button"
          onClick={handleReactivate}
          className="flex items-center justify-center gap-1.5 px-3 py-2 text-[12.5px] font-extrabold text-[color:var(--cf-success-text)] hover:bg-[color:var(--cf-success-soft)] transition-colors"
          data-testid={`standby-reactivate-${d.id}`}
        >
          <ArrowClockwise size={13} weight="bold" />
          Réactiver
        </button>
        <div className="relative border-l border-slate-100">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-[12.5px] font-extrabold text-slate-700 hover:bg-slate-50 transition-colors"
            data-testid={`standby-reporter-${d.id}`}
          >
            <CalendarPlus size={13} weight="bold" />
            Reporter
          </button>
          <ReporterMenu open={menuOpen} onClose={() => setMenuOpen(false)} onPick={handleReporter} />
        </div>
      </div>

      {/* Custom date inline */}
      {customOpen && (
        <div
          className="px-3 py-2.5 border-t border-slate-100 bg-slate-50 flex items-center gap-2"
          data-testid={`standby-custom-${d.id}`}
        >
          <input
            type="date"
            value={customDate}
            onChange={(e) => setCustomDate(e.target.value)}
            className="flex-1 px-2 py-1.5 text-[12.5px] rounded-md border border-slate-200"
            min={todayIso}
            data-testid={`standby-custom-input-${d.id}`}
          />
          <button
            type="button"
            onClick={() => customDate && saveReporter(customDate)}
            disabled={!customDate}
            className="px-3 py-1.5 text-[12px] font-extrabold rounded-md bg-violet-600 text-white disabled:opacity-50"
            data-testid={`standby-custom-save-${d.id}`}
          >
            Valider
          </button>
          <button
            type="button"
            onClick={() => { setCustomOpen(false); setCustomDate(""); }}
            className="p-1.5 text-slate-500 hover:text-slate-900"
            aria-label="Annuler"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function DossiersStandby() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const todayIso = new Date().toISOString().slice(0, 10);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/dossiers?standby=true");
      setItems(res.data || []);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Tri stable : À reprendre > À revoir > Reprise proche > Sans date > Reprise lointaine
  // (puis CA desc, étoiles desc).
  const sorted = [...items]
    .map((d) => ({ d, kind: getRepriseInfo(d, todayIso).kind }))
    .sort((a, b) => {
      const pa = REPRISE_KIND[a.kind].priority;
      const pb = REPRISE_KIND[b.kind].priority;
      if (pa !== pb) return pa - pb;
      const caA = a.d.ca_net || 0;
      const caB = b.d.ca_net || 0;
      if (caA !== caB) return caB - caA;
      return (b.d.etoiles || 0) - (a.d.etoiles || 0);
    })
    .map((x) => x.d);

  const counts = (() => {
    const c = { due: 0, review: 0, soon: 0, none: 0, later: 0 };
    items.forEach((d) => { c[getRepriseInfo(d, todayIso).kind]++; });
    return c;
  })();

  // Optimistic : retire de la liste localement après réactivation
  const handleReactivate = (id) => {
    setItems((prev) => prev.filter((d) => d.id !== id));
  };

  // Met à jour la date until localement après report
  const handleReporter = (id, newUntil) => {
    setItems((prev) => prev.map((d) => (d.id === id ? { ...d, standby_until: newUntil } : d)));
  };

  return (
    <Layout title="Dossiers en stand-by" centerTitle>
      <button
        onClick={() => navigate("/")}
        className="hidden md:inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-5 font-semibold"
        data-testid="standby-back"
      >
        ← Retour
      </button>
      <div className="hidden md:block mb-5">
        <div className="overline">Stand-by</div>
        <h1 className="font-display text-4xl font-extrabold text-slate-900">File d&apos;attente intelligente</h1>
        <p className="text-sm text-slate-500 mt-1">
          Réactive, reporte ou laisse dormir — chaque dossier en sommeil doit pouvoir être décidé en moins de 10 secondes.
        </p>
      </div>

      <DossiersTabs activeKey="standby" />

      {/* KPIs */}
      {!loading && items.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-4" data-testid="standby-totals">
          <div className="card-base p-3">
            <div className="overline text-[9px]">En stand-by</div>
            <div className="metric-value text-2xl text-violet-700">{items.length}</div>
          </div>
          <div className="card-base p-3">
            <div className="overline text-[9px]">À reprendre</div>
            <div className="metric-value text-2xl text-red-700">{counts.due}</div>
          </div>
          <div className="card-base p-3">
            <div className="overline text-[9px]">À revoir</div>
            <div className="metric-value text-2xl text-amber-700">{counts.review}</div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-3" data-testid="standby-loading">
          {[1, 2, 3].map((i) => <div key={i} className="card-base h-28 animate-pulse" />)}
        </div>
      ) : sorted.length === 0 ? (
        <div className="card-base p-8 text-center text-slate-500" data-testid="standby-empty">
          Aucun dossier en stand-by.
          <div className="text-xs text-slate-400 mt-1">
            Utilise « Mettre en stand-by » depuis une fiche dossier pour mettre un dossier en sommeil.
          </div>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="standby-list">
          {sorted.map((d) => (
            <StandbyCard
              key={d.id}
              d={d}
              todayIso={todayIso}
              onReactivate={handleReactivate}
              onReporter={handleReporter}
            />
          ))}
        </div>
      )}
    </Layout>
  );
}
